import { exportJSON } from './exporters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/handoff`;
const SIZE = 900000;
const ALPHA = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const j = async (r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); };
export const fmtCode = (c) => `${c.slice(0, 5)}-${c.slice(5)}`;
export const fmtKey = (k) => k.match(/.{1,4}/g).join('-');
export const cleanCode = (c) => String(c || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
// key only in the link fragment (#k=…) – browsers never send it to the server
export const pickupLink = (code, key) => `${window.location.origin}/?pickup=${code}#k=${key}`;
export const linkKey = () => cleanCode(new URLSearchParams(window.location.hash.slice(1)).get('k'));
// typed code: 10 chars pick-up id + 16 chars key
export const splitCode = (c) => { const s = cleanCode(c); return s.length === 26 ? [s.slice(0, 10), s.slice(10)] : [s, '']; };

const b64 = (u8) => { let s = ''; for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode(...u8.subarray(i, i + 0x8000)); return btoa(s); };
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
async function aesKey(key, salt) {
  const base = await crypto.subtle.importKey('raw', new TextEncoder().encode(key), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', salt: new TextEncoder().encode(`giss-handoff:${salt}`), iterations: 150000, hash: 'SHA-256' }, base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
export async function seal(text, key, salt) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await aesKey(key, salt), new TextEncoder().encode(text));
  return `E1:${b64(iv)}:${b64(new Uint8Array(ct))}`;
}
export async function open(sealed, key, salt) {
  const [, iv, ct] = sealed.split(':');
  return new TextDecoder().decode(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(iv) }, await aesKey(key, salt), unb64(ct)));
}

// phone side: encrypt the whole work (incl. photos + name) on the device, upload in chunks
export async function sendWork(project, onProgress) {
  const raw = await exportJSON(project, true), s = typeof raw === 'string' ? raw : await new Blob([raw]).text();
  const key = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => ALPHA[b % 32]).join('');
  const { code } = await j(await fetch(`${API}/start`, { method: 'POST' }));
  const box = await seal(JSON.stringify({ name: project.title || '', data: s }), key, code);
  const n = Math.max(1, Math.ceil(box.length / SIZE));
  for (let i = 0; i < n; i++) {
    await j(await fetch(`${API}/${code}/chunk`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ i, data: box.slice(i * SIZE, (i + 1) * SIZE) }) }));
    onProgress?.((i + 1) / n);
  }
  await j(await fetch(`${API}/${code}/finish`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ n, name: '' }) }));
  return { code, key };
}

// PC side: download, decrypt locally, hand the file to the normal import, then delete it on the server
export async function pickupWork(code, key, importFile, onProgress) {
  const info = await j(await fetch(`${API}/${code}`));
  if (!info.done) throw new Error('not_ready');
  let s = '';
  for (let i = 0; i < info.n; i++) { s += (await j(await fetch(`${API}/${code}/chunk/${i}`))).data; onProgress?.((i + 1) / info.n); }
  if (!s.startsWith('E1:') || !key) throw new Error('key');
  const { name, data } = JSON.parse(await open(s, key, code).catch(() => { throw new Error('key'); }));
  if (!(await importFile(new File([data], `${name || 'tour'}.giss.json`, { type: 'application/json' }), true))) throw new Error('import');
  await fetch(`${API}/${code}`, { method: 'DELETE' }).catch(() => {});
  return name;
}
